import { useState } from 'react'
import { apiClient, ApiClientError } from '@/lib/api/client'
import type { Inquiry, InquiryCustomerAssociationPreview } from '@/lib/api/types'
import { allCustomerChoices, customerIdentityLabel } from '@/lib/customer-identity'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { CustomerCombobox } from '@/src/components/CustomerCombobox'
import { useAsyncData } from '@/src/hooks/useAsyncData'

type Props = { inquiry: Pick<Inquiry, 'id' | 'customer_id' | 'customer'>; onChanged: () => void }
const blockLabels: Record<string, string> = {
  HUMAN_ADMIN_REQUIRED: 'A signed-in administrator must review this change.',
  CURRENT_CUSTOMER_NOT_PROVISIONAL:
    'The current customer is confirmed. This review only supports provisional identities.',
  TARGET_CUSTOMER_ARCHIVED: 'The selected customer is archived.',
  CURRENT_CUSTOMER_ARCHIVED: 'The current provisional customer is archived.',
  SAME_CUSTOMER: 'Choose a different confirmed customer for this review.',
  TARGET_CUSTOMER_PROVISIONAL: 'Choose a confirmed customer, not another provisional identity.',
  WHATSAPP_SOURCE_BOUND:
    'WhatsApp sources or a customer group bind this inquiry to its current customer. An operator must review the source boundaries.',
  CUSTOMER_BOUND_RECORDS:
    'Existing customer-bound records require a separate review before reassociation.',
  EXECUTION_OR_FINANCIAL_STATE:
    'Financial or execution activity prevents changing the customer through this workflow.',
}

function AssociationReview({ inquiry, onChanged, onCancel }: Props & { onCancel: () => void }) {
  const { data, loading, error, reload } = useAsyncData(
    () => allCustomerChoices((page) => apiClient.listCustomersPage(page, { limit: 100 })),
    [],
  )
  const [targetId, setTargetId] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<InquiryCustomerAssociationPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()
  const choices = (data ?? []).filter(
    (customer) =>
      customer.id !== inquiry.customer_id && !customer.is_placeholder && !customer.deleted_at,
  )
  const target = choices.find((customer) => customer.id === targetId)

  async function review() {
    if (!target || busy) return
    setBusy(true)
    setPreview(null)
    setActionError(null)
    try {
      const result = await apiClient.previewInquiryCustomerAssociation(inquiry.id, target.id)
      if (
        result.inquiry_id !== inquiry.id ||
        result.current_customer.id !== inquiry.customer_id ||
        result.target_customer.id !== target.id
      )
        throw new Error(
          'The review does not match this inquiry and customer. Refresh the inquiry before continuing.',
        )
      setPreview(result)
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : 'Customer association review is unavailable.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function apply() {
    if (!preview?.allowed || !reason.trim() || busy || preview.target_customer.id !== targetId)
      return
    if (
      !(await confirm({
        title: 'Change this inquiry’s customer?',
        description: `${customerIdentityLabel(preview.current_customer)} → ${customerIdentityLabel(preview.target_customer)}. Both customer records and their numbers remain. No contacts, groups or source messages are moved or merged. Reason: ${reason.trim()}`,
        action: 'Confirm customer association',
      }))
    )
      return
    setBusy(true)
    setActionError(null)
    try {
      await apiClient.applyInquiryCustomerAssociation(inquiry.id, {
        target_customer_id: preview.target_customer.id,
        expected_current_customer_id: preview.current_customer.id,
        expected_inquiry_updated_at: preview.inquiry_updated_at,
        expected_current_customer_updated_at: preview.current_customer.updated_at,
        expected_target_customer_updated_at: preview.target_customer.updated_at,
        reason: reason.trim(),
      })
      onChanged()
      onCancel()
    } catch (error) {
      setPreview(null)
      setActionError(
        error instanceof ApiClientError && error.status < 500
          ? `${error.message} Preview the association again before confirming.`
          : 'The change outcome is uncertain. Refresh the inquiry before trying again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-label="Customer association review" className="space-y-3 rounded border p-3">
      <p className="font-medium">Associate this inquiry with an existing customer</p>
      <p className="text-xs text-muted-foreground">
        Verify the identity independently. This changes only an eligible inquiry’s customer
        association and preserves both customer IDs.
      </p>
      {loading && <p role="status">Loading customer choices…</p>}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
          <Button variant="outline" onClick={reload}>
            Refresh customer choices
          </Button>
        </Alert>
      )}
      <Field>
        <FieldLabel htmlFor="association-customer">Confirmed customer</FieldLabel>
        <CustomerCombobox
          id="association-customer"
          customers={choices}
          value={targetId}
          onValueChange={(value) => {
            setTargetId(value)
            setPreview(null)
            setActionError(null)
          }}
          disabled={loading || busy || Boolean(error)}
          placeholder="Search by name, contact or customer ID"
        />
      </Field>
      <Button
        variant="outline"
        disabled={!target || busy || Boolean(error)}
        onClick={() => void review()}
      >
        Preview association
      </Button>
      {preview && (
        <div className="space-y-3">
          <dl className="space-y-2">
            <div>
              <dt className="text-xs text-muted-foreground">Current provisional customer</dt>
              <dd>{customerIdentityLabel(preview.current_customer)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Reviewed target customer</dt>
              <dd>{customerIdentityLabel(preview.target_customer)}</dd>
            </div>
          </dl>
          {!preview.allowed ? (
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-inside list-disc">
                  {preview.block_reasons.length ? (
                    preview.block_reasons.map((code) => (
                      <li key={code}>
                        {blockLabels[code] || 'This association needs a separate operator review.'}
                      </li>
                    ))
                  ) : (
                    <li>This association is blocked. An operator must review the records.</li>
                  )}
                </ul>
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <Field>
                <FieldLabel htmlFor="association-reason">Reason for association</FieldLabel>
                <Textarea
                  id="association-reason"
                  rows={3}
                  maxLength={2000}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  disabled={busy}
                />
              </Field>
              <Button disabled={busy || !reason.trim()} onClick={() => void apply()}>
                Confirm reviewed association
              </Button>
            </>
          )}
        </div>
      )}
      {actionError && (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={busy} onClick={onChanged}>
          Refresh inquiry
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel association review
        </Button>
      </div>
      {dialog}
    </section>
  )
}

/** Caller renders this only for a human administrator; every server step rechecks authorization. */
export function InquiryCustomerAssociation(props: Props) {
  const [open, setOpen] = useState(false)
  if (!props.inquiry.customer?.is_placeholder) return null
  return open ? (
    <AssociationReview {...props} onCancel={() => setOpen(false)} />
  ) : (
    <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
      Review customer association
    </Button>
  )
}
